import React, { forwardRef } from 'react'
import QRCodeGenerator from './QRCodeGenerator'
import {
  getResidentPhotoUrl,
  getResidentInitials,
} from '../../utils/photoUtils'

const ModernDigitalIDCard = forwardRef(
  ({ resident, showBack = false, qrDataUrl }, ref) => {
    const getPhotoUrl = (resident) => {
      return getResidentPhotoUrl(resident)
    }

    const qrData = {
      id: resident.id,
      barangayId: resident.barangay_id_number,
      name: `${resident.first_name} ${resident.last_name}`,
      birthdate: resident.date_of_birth,
      address: resident.address,
      contactNumber: resident.phone_number || resident.contact_number,
      emergencyContact: resident.emergency_contact_name,
      emergencyContactNumber: resident.emergency_contact_number,
      issued: new Date().toISOString(),
      expires: '2025-12-31',
    }

    const formatAddress = (resident) => {
      return (
        [
          resident.house_number,
          resident.street,
          resident.purok_zone,
          resident.barangay,
          resident.municipality,
          resident.province,
        ]
          .filter(Boolean)
          .join(', ') ||
        resident.address ||
        'Address not provided'
      )
    }

    const calculateAge = (birthdate) => {
      if (!birthdate) return 'N/A'
      const today = new Date()
      const birth = new Date(birthdate)
      let age = today.getFullYear() - birth.getFullYear()
      const monthDiff = today.getMonth() - birth.getMonth()
      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birth.getDate())
      ) {
        age--
      }
      return age
    }

    const securityNumber = `${resident.id
      .toString()
      .padStart(4, '0')}-${new Date().getFullYear()}`

    const FrontSide = () => (
      <div
        ref={ref}
        className='digital-id-card mb-3 border-2 border-blue-600 rounded-lg overflow-hidden relative flex flex-col'
        style={{
          width: '3.675in',
          height: '2.325in',
          fontSize: '7px',
          fontFamily: 'Arial, sans-serif',
        }}
      >
        <div className='absolute inset-0 opacity-5'>
          <div
            className='w-full h-full bg-repeat'
            style={{
              backgroundSize: '10px 10px',
            }}
          />
        </div>

        <div className='bg-gradient-to-r from-blue-600 to-blue-800 text-white p-1 text-center relative z-10'>
          <div style={{ fontSize: '9px', lineHeight: '1' }}>
            <div className=''>REPUBLIC OF THE PHILIPPINES</div>
            <div style={{ fontSize: '9px' }}>PROVINCE</div>
            <div style={{ fontSize: '9px' }}>MUNICIPALITY/CITY</div>
            <div style={{ fontSize: '13px' }}>BARANGAY 365</div>
            <div className='font-bold mt-0.5' style={{ fontSize: '9px' }}>
              DIGITAL ID CARD
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className='p-1.5 relative z-10 flex-1 flex flex-col justify-between'>
          <div className='flex gap-1'>
            {/* Photo Section */}
            <div className='flex-shrink-0'>
              <div className='w-24 h-24 bg-gray-200 border border-gray-400 rounded overflow-hidden'>
                {getPhotoUrl(resident) ? (
                  <img
                    src={getPhotoUrl(resident)}
                    alt='ID Photo'
                    className='w-full h-full object-cover'
                    onError={(e) => {
                      e.target.style.display = 'none'
                      e.target.parentNode.querySelector(
                        '.fallback-avatar'
                      ).style.display = 'flex'
                    }}
                  />
                ) : null}
                <div
                  className={`fallback-avatar w-full h-full bg-gradient-to-r from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold ${
                    getPhotoUrl(resident) ? 'hidden' : 'flex'
                  }`}
                  style={{ fontSize: '20px' }}
                >
                  {getResidentInitials(resident)}
                </div>
              </div>
            </div>
            <div className='flex-1 ml-1 mt-2'>
              <div className='space-y-2 text-left'>
                <div
                  style={{ fontSize: '10px', lineHeight: '1' }}
                  className='space-y-0.5'
                >
                  <span className='text-gray-600'>ID Number:</span>
                  <span className='ml-1 text-blue-900 font-bold'>
                    {resident.barangay_id_number}
                  </span>
                </div>
                <div
                  className='font-bold text-gray-900 mt-2'
                  style={{ fontSize: '12px', lineHeight: '1' }}
                >
                  {resident.first_name} {resident.middle_name}{' '}
                  {resident.last_name} {resident.suffix}
                </div>
                <div style={{ fontSize: '9px' }} className='space-y-0.5'>
                  <div className='m-0'>
                    <span className='text-gray-600'>Date of Birth:</span>
                    <span className='ml-1'>
                      {resident.date_of_birth
                        ? new Date(resident.date_of_birth).toLocaleDateString(
                            'en-US',
                            {
                              month: '2-digit',
                              day: '2-digit',
                              year: '2-digit',
                            }
                          )
                        : 'N/A'}
                    </span>
                    <span className='text-gray-600 ml-2'>Age:</span>
                    <span className='ml-1' style={{ fontSize: '10px' }}>
                      {calculateAge(resident.date_of_birth)}
                    </span>
                  </div>
                  <div className='m-0'>
                    <span className='text-gray-600'>Gender:</span>
                    <span
                      className='ml-1 uppercase'
                      style={{ fontSize: '10px' }}
                    >
                      {resident.gender || 'N/A'}
                    </span>
                    <span className='text-gray-600 ml-2'>Civil Status:</span>
                    <span
                      className='ml-1 uppercase'
                      style={{ fontSize: '10px' }}
                    >
                      {resident.civil_status || 'N/A'}
                    </span>
                  </div>
                  <div className='text-gray-600'>Address:</div>
                  <div
                    className='text-gray-900'
                    style={{ fontSize: '10px', lineHeight: '1.1' }}
                  >
                    {formatAddress(resident).substring(0, 80)}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className='flex justify-between items-end mt-2'>
            <div className='text-center' style={{ fontSize: '8px' }}>
              <div className='border-t border-gray-400 mt-2 pt-0.5 w-20'>
                <div>Signature</div>
              </div>
            </div>
            <div className='text-center' style={{ fontSize: '8px' }}>
              <div className='border-t border-gray-400 mt-2 pt-0.5 w-20'>
                <div>Punong Barangay</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )

    const BackSide = () => (
      <div
        className='digital-id-card bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-blue-600 rounded-lg overflow-hidden relative'
        style={{
          width: '3.675in',
          height: '2.325in',
          fontSize: '9px',
          fontFamily: 'Arial, sans-serif',
        }}
      >
        <div className='bg-gradient-to-r from-blue-600 to-blue-800 text-white p-1 text-center relative z-10'>
          <div className='font-bold' style={{ fontSize: '9px' }}>
            DIGITAL ID CARD
          </div>
        </div>

        <div className='p-2 relative z-10 h-full flex space-x-2 bg-white'>
          <div className='flex-1 space-y-1 text-left'>
            <div>
              <div className='font-bold text-gray-700 text-[8px]'>
                EMERGENCY CONTACT
              </div>
              <div className='text-[8px] space-y-0.5'>
                <div>
                  <span className='text-gray-600'>Name:</span>
                  <span
                    className='ml-1'
                    style={{ fontSize: '11px', lineHeight: '1.1' }}
                  >
                    {resident.emergency_contact_name || 'N/A'}
                  </span>
                </div>
                <div>
                  <span className='text-gray-600'>Phone:</span>
                  <span
                    className='ml-1'
                    style={{ fontSize: '11px', lineHeight: '1.1' }}
                  >
                    {resident.emergency_contact_number || 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            <div className='border-t border-gray-300 border-1 pt-1'>
              <div className='font-bold text-gray-700 text-[9px]'>
                ADDITIONAL INFO
              </div>
              <div className='text-[9px] space-y-0.5'>
                <div>
                  <span className='text-gray-600'>Contact Number:</span>
                  <span
                    className='ml-1'
                    style={{ fontSize: '11px', lineHeight: '1.1' }}
                  >
                    {resident.phone_number || resident.contact_number || 'N/A'}
                  </span>
                </div>
                <div>
                  <span className='text-gray-600'>Household:</span>
                  <span
                    className='ml-1'
                    style={{ fontSize: '11px', lineHeight: '1.1' }}
                  >
                    {resident.household_number || 'N/A'}
                  </span>
                </div>
                <div>
                  <span className='text-gray-600'>Occupation:</span>
                  <span
                    className='ml-1'
                    style={{ fontSize: '11px', lineHeight: '1.1' }}
                  >
                    {resident.occupation || 'N/A'}
                  </span>
                </div>
                {console.log('res', resident)}
                <div>
                  <span className='text-gray-600'>Wallet ID:</span>
                  <span
                    className='ml-1'
                    style={{ fontSize: '8.7px', lineHeight: '1.1' }}
                  >
                    {resident.wallet_address || 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            <div className='border-t border-gray-300 pt-1'>
              <div className='font-bold text-gray-700 text-[9px]'>
                TERMS & CONDITIONS
              </div>
              <div className='text-gray-600 leading-tight text-[8px]'>
                <div>• This ID is property of Barangay Binakod</div>
                <div>• Must be returned when expired</div>
                <div>• Report immediately if lost or stolen</div>
                <div>• Valid for barangay transactions only</div>
              </div>
            </div>
          </div>

          <div className='flex gap-1'>
            <div className='flex-shrink-0'>
              <div className='w-30 h-30 rounded overflow-hidden flex items-center justify-center'>
                {qrDataUrl ? (
                  <img
                    src={qrDataUrl}
                    alt='QR Code'
                    style={{ width: 75, height: 75 }}
                  />
                ) : (
                  <QRCodeGenerator data={qrData} size={90} />
                )}
              </div>

              <div className='text-gray-600 text-[8px] mt-2'>
                <div>Issued: {new Date().toLocaleDateString()}</div>
                <div>
                  Expires:{' '}
                  {(() => {
                    const issued = new Date()
                    const expires = new Date(issued)
                    expires.setFullYear(issued.getFullYear() + 1)
                    return expires.toLocaleDateString()
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )

    return showBack ? <BackSide /> : <FrontSide />
  }
)

ModernDigitalIDCard.displayName = 'ModernDigitalIDCard'

export default ModernDigitalIDCard
